import { FC, ReactElement, ReactNode } from 'react';

import { Box, Stack, SxProps, Typography } from '@mui/material';
import { CaretDown, CaretUp } from 'phosphor-react';
import { useMeasure } from 'react-use';

import { primaryColors } from '~components/theme/styles/colors';

const CaretCollapse: FC<{ collapsed: boolean; size: number }> = ({ collapsed, size }) => {
  return collapsed ? <CaretDown size={size} /> : <CaretUp size={size} />;
};

export const CollapsableContainer: FC<{
  collapsed: boolean;
  contentSeperator?: boolean;
  title: string | ReactElement;
  titleSx?: SxProps;
  titleVariant?: string;
  titleContainerSx?: SxProps;
  contentContainerSx?: SxProps;
  caretSize?: number;
  hideCaret?: boolean;
  caretPlacement?: 'left' | 'right';
  children: ReactNode;
  onCollapse: () => void;
}> = ({
  caretPlacement = 'right',
  hideCaret,
  collapsed,
  title,
  titleSx,
  caretSize = 24,
  titleVariant = 'h4',
  children,
  titleContainerSx,
  contentContainerSx,
  onCollapse,
}) => {
  const [ref, { height }] = useMeasure();

  return (
    <Stack>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        sx={{ zIndex: 2, cursor: 'pointer', ...titleContainerSx }}
        onClick={onCollapse}
      >
        {caretPlacement === 'left' && !hideCaret ? (
          <CaretCollapse size={caretSize} collapsed={collapsed} />
        ) : null}
        {typeof title === 'string' ? (
          <Typography
            sx={{ transition: 'color 0.15s linear', userSelect: 'none', ...titleSx }}
            variant={titleVariant as any}
            color={collapsed ? primaryColors[700] : primaryColors[100]}
          >
            {title}
          </Typography>
        ) : (
          <Box sx={titleSx}>{title}</Box>
        )}
        {caretPlacement === 'right' && !hideCaret ? (
          <CaretCollapse size={caretSize} collapsed={collapsed} />
        ) : null}
      </Stack>
      <Box
        sx={{
          transition: 'height 0.2s ease-out',
          height: collapsed ? 0 : height,
          overflowY: 'hidden',
          ...contentContainerSx,
        }}
      >
        <Box ref={ref} sx={{ minHeight: height }}>
          {children}
        </Box>
      </Box>
    </Stack>
  );
};
